import { first, isArray, find } from "lodash"
import dayjs from "dayjs"

const mapPageData = (data) => {
  const parentCountry = first(data.country)

  if (data) {
    return {
      ...data,
      groups: data?.groups || false,
      parentCountryId: parentCountry?.external_id,
      destinationId: parentCountry?.destinations?.[0]?.title,
      hero: {
        ...data?.hero,
        subtitleImageUrl: data.hero?.background_image?.variants?.[0]?.url,
        location: data.hero?.images?.[0]?.variants?.[0]?.title,
      },
      content_highlight: {
        ...data?.content_highlight,
        color: data.content_highlight?.background_color,
      },
      vmedia_content: {
        images: data.vmedia_content?.media,
      },
      facts_overview: {
        ...data.facts_overview,
        items: [
          { title: "Population", value: data.facts_overview?.population },
          { title: "Languages", value: data.facts_overview?.official_languages },
          { title: "Currency", value: data.facts_overview?.currency },
          { title: "Capital", value: data.facts_overview?.capital },
        ].filter((item) => Boolean(item.value)),
      },
    }
  }
  return null
}

const mapCountryData = (country) => {
  if (country) {
    return {
      ...country,
      destinationId: country.destinations?.[0]?.title,
      hero: {
        ...country.hero,
        subtitleImageUrl: country.hero?.background_image?.variants?.[0]?.url,
        location: country.hero?.images?.[0]?.variants?.[0]?.title,
        breadcrumbs: [
          {
            title: "Home",
            uri: "/",
          },
          {
            title: "Destinations",
            uri: "/destinations",
          },
          ...(country?.destinations?.[0]
            ? [
                {
                  title: country?.destinations?.[0]?.title,
                  uri: country?.destinations?.[0]?.slug,
                },
              ]
            : []),
          {
            title: country?.title,
          },
        ],
      },
      content_highlight: {
        ...country.content_highlight,
        color: country.content_highlight?.background_color,
      },
      vmedia_content: {
        images: country.vmedia_content?.media,
      },
      facts_overview: {
        ...country.facts_overview,
        items: [
          { title: "Population", value: country.facts_overview?.population },
          { title: "Languages", value: country.facts_overview?.official_languages },
          { title: "Currency", value: country.facts_overview?.currency },
          { title: "Capital", value: country.facts_overview?.capital },
        ],
      },
      explore_list: {
        ...country.explore_list,
        items:
          country.explore_list?.items?.map((item) => ({
            ...item,
            image: item?.hero?.images?.[0],
          })) || [],
      },
      suggested_trips: {
        ...country.suggested_trips,
        link: { title: "All Experiences", uri: "/experiences" },
      },
    }
  }
  return null
}

const mapGeoPageData = (travelInformation) => {
  if (travelInformation) {
    return {
      ...travelInformation,
      hero: {
        ...travelInformation.hero,
        subtitleImageUrl: travelInformation.hero?.background_image?.variants?.[0]?.url,
        location: travelInformation.hero?.images?.[0]?.variants?.[0]?.title,
        breadcrumbs: [
          {
            title: "Home",
            uri: "/",
          },
          {
            title: "Destinations",
            uri: "/destinations",
          },
          ...(travelInformation?.country?.[0]?.destinations?.[0]
            ? [
                {
                  title: travelInformation?.country?.[0]?.destinations?.[0]?.title,
                  uri: travelInformation?.country?.[0]?.destinations?.[0]?.slug,
                },
              ]
            : []),
          ...(travelInformation?.country?.[0]
            ? [
                {
                  title: travelInformation?.country?.[0]?.title,
                  uri: travelInformation?.country?.[0]?.slug,
                },
              ]
            : []),
          {
            title: travelInformation?.title,
          },
        ],
      },
    }
  }
  return null
}

const mapArticleData = (article) => {
  if (article) {
    const articleTags = []
    const heroTags = []
    if (article?.article_category) {
      article?.article_category?.forEach((item) => {
        articleTags.push({ title: item?.title, uri: item?.slug })
        heroTags.push({ title: item?.title, uri: item?.slug })
      })
    }

    if (article?.country) {
      article?.country?.forEach((item) =>
        articleTags.push({ title: item?.title, uri: item?.slug, icon: "location" })
      )
    }

    if (article?.destinations) {
      article?.destinations?.forEach((item) =>
        articleTags.push({ title: item?.title, uri: item?.slug, icon: "location" })
      )
    }

    return {
      ...article,
      image: article?.featured_images?.[0],
      location: article?.featured_images?.[0]?.variants?.[0]?.title,
      tags: articleTags,
      author: article?.authored_by,
      shareLinks: {
        blogLink: {
          title: article?.article_type?.link?.title,
          uri: article?.article_type?.slug,
        },
        title: article?.title,
        type: "article",
      },
      published: dayjs(article?.created).format("YYYY-MM-DD"),
      article_category: heroTags,
    }
  }
}

const mapArticleCategoryData = (articleCategory) => {
  if (articleCategory) {
    return {
      ...articleCategory,
      hero: {
        ...articleCategory.hero,
        subtitleImageUrl: articleCategory.hero?.background_image?.variants?.[0]?.url,
        location: articleCategory.hero?.images?.[0]?.variants?.[0]?.title,
        path: [
          {
            title: "Home",
            uri: "/",
          },
          {
            title: "Inspiration",
            uri: "/inspiration",
          },
          { title: articleCategory?.title },
        ],
      },
      featured_article: {
        ...articleCategory?.featured_article?.featured_article,
        image: articleCategory?.featured_article?.featured_article?.featured_images?.[0],
        published: dayjs(articleCategory?.featured_article?.created).format("YYYY-MM-DD"),
        author: articleCategory?.featured_article?.featured_article?.authored_by?.title,
        authorSlug: articleCategory?.featured_article?.featured_article?.authored_by?.slug,
      },
    }
  }
}

const mapArticleCategories = (categories) => {
  if (Array.isArray(categories) || categories?.length > 0) {
    return categories.map((category) => ({
      title: category?.title,
      uri: category?.slug,
    }))
  }
}

const mapStaffData = (data) => {
  if (data) {
    if (data?.explore_list) {
      data.explore_list.items = data.explore_list.items.map((item) => {
        if (item?.hero) {
          item.image = item.hero.images?.[0]
        }
        return item
      })
    }

    data.items = []
    if (data?.fun_facts) {
      data.items.push({
        title: "Fun Facts",
        text: data?.fun_facts,
      })
    }
    if (data?.favorite_quote) {
      data.items.push({
        title: "Favourite Quote",
        text: data?.favorite_quote,
      })
    }
    if (data?.optional_category_label) {
      data.items.push({
        title: data?.optional_category_label,
        text: data?.optional_category_content,
      })
    }
    data.breadcrumbs = [
      {
        title: "Home",
        uri: "/",
      },
      {
        title: "Our Experts",
        uri: "/our-experts",
      },
    ]
    if (data?.staff_department?.[0]) {
      data.breadcrumbs.push({
        title: data?.staff_department[0]?.title,
        uri: data?.staff_department[0]?.slug,
      })
    }

    data.breadcrumbs.push({ title: data.title })

    return {
      ...data,
      destinationId: data.destinations?.[0]?.title,
      description: data.bio,
      tags: data.destinations,
      image: data.photo,
    }
  }
  return null
}

const mapStaffDepartmentData = (data) => {
  if (data) {
    return {
      ...data,
      hero: {
        ...data.hero,
        subtitleImageUrl: data.hero?.background_image?.variants?.[0]?.url,
        location: data.hero?.images?.[0]?.variants?.[0]?.title,
        path: [
          {
            title: "Home",
            uri: "/",
          },
          ...(data?.staff_type
            ? [
                {
                  title: data?.staff_type?.title,
                  uri: data?.staff_type?.slug,
                },
              ]
            : []),
          { title: data?.title },
        ],
      },
    }
  }
  return null
}

const mapStaffTypeData = (data) => {
  if (data) {
    data.breadcrumbs = [
      {
        title: "Home",
        uri: "/",
      },
    ]
    data.breadcrumbs.push({ title: data.title })
    return {
      ...data,
      hero: { ...data.hero, media: data.hero?.images },
    }
  }
  return null
}

const mapHomepageData = (homepage) => {
  if (homepage) {
    return {
      ...homepage,
      hero: {
        ...homepage.hero,
        location: homepage.hero?.images?.[0]?.variants?.[0]?.title,
      },
      ...(homepage.teaser_3up && {
        teaser_3up: {
          ...homepage.teaser_3up,
          items: homepage.teaser_3up?.article_list_override?.map((item) => {
            item.image = item?.featured_images?.[0]
            return item
          }),
        },
      }),
    }
  }
  return null
}

const mapLandingPageData = (landingPage) => {
  if (landingPage) {
    return {
      ...landingPage,
      hero: {
        ...landingPage.hero,
        location: landingPage.hero?.images?.[0]?.variants?.[0]?.title,
        subtitleImageUrl: landingPage.hero?.background_image?.variants?.[0]?.url,
        path: [
          {
            title: "Home",
            uri: "/",
          },
          ...(landingPage?.parent_page
            ? [{ title: landingPage?.parent_page?.title, uri: landingPage?.parent_page?.slug }]
            : []),
          { title: landingPage?.title },
        ],
      },
    }
  }
  return null
}

const mapTravelStyleItems = (items) => {
  if (items) {
    return items.map((item) => ({
      label: item?.title,
      uri: item?.slug,
      image: item?.hero?.images?.[0],
      icon: item?.icon,
    }))
  }
  return []
}

const mapPlacesItems = (items) => {
  if (items) {
    return items.map((item) => {
      const placeImages = item?.hero?.images?.[0]
      const teaserVariant = find(placeImages?.variants, { style: "teaser_3xl" })
      const optimalImage = teaserVariant
        ? {
            variants: [
              {
                ...teaserVariant,
                style: "default",
              },
            ],
          }
        : {
            variants: [],
          }

      return {
        title: item?.title,
        link: { title: item?.title, uri: item?.slug },
        image: optimalImage,
      }
    })
  }
  return []
}

const mapPlacesToGOData = (data) => {
  const result = { ...data, ...data?.template }
  delete result.template
  if (result) {
    result.breadcrumbs = [
      {
        title: "Home",
        uri: "/",
      },
      {
        title: "Destinations",
        uri: "/destinations",
      },
    ]
    result.destinationId = result.parent_page?.destinations?.[0]?.id
    if (result?.parent_page?.destinations?.[0]) {
      result.breadcrumbs.push({
        title: result.parent_page?.destinations?.[0]?.title || "Unknown",
        uri: result.parent_page?.destinations?.[0]?.slug || "#",
      })
    }

    if (result?.parent_page) {
      result.breadcrumbs.push({
        title: result.parent_page?.title || "Unknown",
        uri: result.parent_page?.link?.uri || "#",
      })
    }

    result.breadcrumbs.push({
      title: result.title,
    })
  }

  return result
}

const mapDestinationCollections = (destinations) => {
  if (destinations) {
    return destinations.map((destination) => ({
      title: destination?.title,
      link: destination?.slug,
      image: destination?.hero?.images?.[0],
    }))
  }
  return []
}

const mapCareersListItems = (items) => {
  if (items) {
    return items.map((item) => ({
      title: item?.title,
      link: { uri: item?.uri },
    }))
  }
  return []
}

const mapSectionSwitcher = (sections) => {
  if (sections) {
    return sections.map((section) => ({
      title: section?.title,
      components: section?.components.map((component) => ({
        type: component?.type,
        content: getSectionContent(component),
      })),
    }))
  }
}

const getSectionContent = (section) => {
  switch (section?.type) {
    case "includes_excludes_list":
      return {
        listStyle: "pros-and-cons",
        leftContent: { title: section?.trip_includes_title, items: section?.includes_item },
        rightContent: { title: section?.trip_excludes_title, items: section?.excludes_item },
      }
    case "two_column_text":
      return {
        leftContent: { description: section?.text_body_1 },
        rightContent: { description: section?.text_body_2 },
      }
    case "accordion":
      return {
        title: section?.title,
        items: section?.accordion_items,
      }
    case "split_image_and_text":
      return {
        image: section?.image,
        title: section?.title,
        content: section?.description,
      }
    case "text":
      return {
        title: section?.title,
        content: section?.text,
      }
    case "weather_guide":
      return {
        title: section?.title,
        items: section?.items,
      }
    default:
      return {}
  }
}

const mapWishlistData = (likedArticles, data) => {
  if (!likedArticles) {
    return data
  }
  return (
    data &&
    data.map((article) =>
      likedArticles.includes(Array.isArray(article.nid) ? article.nid[0] : article.nid)
        ? { ...article, liked: true }
        : article
    )
  )
}

const mapFacetsForGeneralSearch = (params) => {
  const facets = []

  if (params?.country) facets.push({ label: params.country?.label })
  if (params?.destination) facets.push({ label: params.destination?.label })
  if (Array.isArray(params?.experiences & (params?.experiences?.length > 0))) {
    params.experiences.forEach((param) => facets.push({ label: param?.label }))
  }

  return facets
}

const mapArticleTeaser = (articleTeasers, selectedEntity, liked) => {
  return [
    ...articleTeasers.map((article) => {
      const nid = Array.isArray(article.nid) ? article.nid[0] : article.nid
      return nid === selectedEntity.nid ? { ...article, liked } : article
    }),
  ]
}

const mapTripTeaser = (items, selectedEntity, liked) => {
  return [
    ...items.map((trip) => {
      const nid = Array.isArray(trip.nid) ? trip.nid[0] : trip.nid
      return nid === selectedEntity.nid ? { ...trip, liked } : trip
    }),
  ]
}

const mapExploreListItems = (items) => {
  if (items) {
    return items.map((item) => ({
      title: item?.title,
      self_link: item?.self_link,
      image: item?.hero?.images?.[0],
    }))
  }
}

const mapTripItinerary = (itinerary) => {
  if (itinerary) {
    return {
      title: itinerary?.package_title,
      subtitle: "Trip Itinerary",
      items: mapTripItineraryDays(itinerary?.p_days),
    }
  }
  return null
}

const filterDayDuplicates = (days) => {
  const uniqueProperties = new Set()
  let previousDayProperties = new Set()

  const filteredDays = days.map((day, index) => {
    const currentDayProperties = new Set()

    const filteredComponents = day.day_component.filter((component) => {
      if (component.c_type === "Property" && component.c_duration) {
        const componentId = component?.c_property_reference_id?.id
        if (uniqueProperties.has(componentId) || previousDayProperties.has(componentId)) {
          if (day.day_number === component.c_start_day) {
            return true
          }
          return false // Remove duplicate property
        } else {
          uniqueProperties.add(componentId)
          currentDayProperties.add(componentId)
          return true // Keep the first occurrence of the property
        }
      } else {
        return true // Keep non-property components
      }
    })

    // Update previous day's properties
    previousDayProperties = currentDayProperties

    return {
      ...day,
      day_component: filteredComponents,
    }
  })

  return filteredDays
}

const filterDayComponentDuplicates = (dayComponents) => {
  const uniqueObjects = {}
  const result = []

  dayComponents.forEach((component) => {
    if (component.c_sequence) {
      if (!uniqueObjects[component.c_sequence]) {
        uniqueObjects[component.c_sequence] = true
        result.push(component)
      }
    } else {
      result.push(component)
    }
  })
  return result
}

const mapTripItineraryDays = (days) => {
  if (days) {
    const filteredDays = filterDayDuplicates(days)
    return filteredDays?.map((day) => ({
      title: day?.day_title,
      dayNumber: day?.day_number,
      details: mapTripItineraryDayDetails(day?.day_component),
    }))
  }
  return null
}

const mapTripItineraryDayDetails = (details) => {
  if (details) {
    const trimTitle = (title) => title?.replace(/ *\([^)]*\) */g, "")
    const filteredDetails = filterDayComponentDuplicates(details)
    return filteredDetails.map((detail) => ({
      id: detail?.c_component_id,
      sequence: detail?.c_sequence,
      title: trimTitle(detail?.c_name),
      name: detail?.c_title,
      type: detail?.c_type ? detail?.c_type.toLowerCase() : null,
      extraTypeId: detail?.c_extra_type_id,
      duration: detail?.c_duration,
      specials: detail?.c_meal || detail?.c_default_meal_basis,
      description1: detail?.c_description,
      description2: detail?.c_description_multiday,
      customerDescription: detail?.c_customer_description,
      mealDescription: detail?.c_meal,
      rating: detail?.c_property_reference_id?.rating,
      propertyTitle: detail?.c_property_reference_id?.title,
      propertyDescription: detail?.c_property_reference_id?.description,
      geolocation: detail?.c_property_reference_id?.geolocation,
      transferDeparture: detail?.c_departure_location_name?.title,
      transferArrival: detail?.c_arrival_location_name?.title,
      image: {
        variants: [
          {
            width: 400,
            height: 400,
            url: `https://admin.goway.app/content/${detail?.c_property_reference_id?.accomodation_image}`,
            title: detail?.c_property_reference_id?.title,
            alt: detail?.c_property_reference_id?.title,
            style: "default",
          },
        ],
      },
    }))
  }
  return null
}

const mapTripItineraryMapItemsFromOverrides = (overrides) => {
  return overrides.map((override) => ({
    label: override?.location_title || "",
    lat: override?.paragraph_geolocation?.lat,
    lng: override?.paragraph_geolocation?.lng,
  }))
}

const mapTripItineraryMapItems = (itinerary, overrides) => {
  if (isArray(overrides) && overrides.length > 0) {
    return mapTripItineraryMapItemsFromOverrides(overrides)
  }
  const itineraryDays = itinerary?.p_days
  if (itineraryDays) {
    const mapItems = []
    itineraryDays.forEach((day) => {
      const dayDetails = day?.day_component
      if (dayDetails) {
        dayDetails.forEach((detail) => {
          const isItemAlreadyAdded = mapItems.find(
            (item) => item.label === detail?.c_property_reference_id?.title
          )
          if (
            detail?.c_type === "Property" &&
            detail?.c_property_reference_id?.geolocation &&
            !isItemAlreadyAdded
          ) {
            mapItems.push({
              label: detail?.c_property_reference_id?.title,
              lat: detail?.c_property_reference_id?.geolocation?.lat,
              lng: detail?.c_property_reference_id?.geolocation?.lng,
            })
          }
        })
      }
    })
    return mapItems
  }
  return null
}

export {
  mapPageData,
  mapCountryData,
  mapStaffData,
  mapStaffDepartmentData,
  mapStaffTypeData,
  mapArticleData,
  mapHomepageData,
  mapTravelStyleItems,
  mapPlacesItems,
  mapPlacesToGOData,
  mapLandingPageData,
  mapDestinationCollections,
  mapCareersListItems,
  mapSectionSwitcher,
  mapArticleCategoryData,
  mapArticleCategories,
  mapWishlistData,
  mapArticleTeaser,
  mapExploreListItems,
  mapFacetsForGeneralSearch,
  mapTripItinerary,
  mapTripItineraryMapItems,
  mapTripTeaser,
  mapGeoPageData,
}
